import liff from '@line/liff';
import dayjs from 'dayjs';
import buddhistEra from "dayjs/plugin/buddhistEra";
import * as XLSX from 'xlsx';

let instantLineLiff = null;

const safeLocalStorage = {
    getItem: (key) => {
        try {
            return localStorage.getItem(key);
        } catch (error) {
            console.error("Error accessing localStorage (getItem):", error);
            return null;
        }
    },
    setItem: (key, value) => {
        try {
            localStorage.setItem(key, value);
        } catch (error) {
            console.error("Error accessing localStorage (setItem):", error);
        }
    },
    removeItem: (key) => {
        try {
            localStorage.removeItem(key);
        } catch (error) {
            console.error("Error accessing localStorage (removeItem):", error);
        }
    }
};

function onSleep(msec) {
    return new Promise((resolve) => setTimeout(() => {
        resolve(true)
    }, msec || 1000))
}

function capitalizeFirstLetter(string) {
    return string.charAt(0).toUpperCase() + string.slice(1);
}

async function onLoginLineLiff(redirectUri) {
    if (process.env.NODE_ENV === 'development') {
        console.log("onLoginLineLiff: CALL", { redirectUri, href: window.location.href });
    }

    if (redirectUri) {
        safeLocalStorage.setItem('react_app_redirect_after_login', redirectUri);
    }

    let response = null;
    const lineLiffId = process.env.REACT_APP_LINE_LIFF_ID || "";
    if (lineLiffId) {
        try {
            instantLineLiff = await liff.init({
                liffId: `${lineLiffId}`,
                withLoginOnExternalBrowser: true,
            }).catch(err => { throw err });

            if (liff.isLoggedIn()) {
                response = {};
                try {
                    response.profile = await liff.getProfile();
                } catch (err) {
                    console.log("onLoginLineLiff.getProfile", err);
                    response.profile = null;
                }
                if (!response.profile) {
                    const target = redirectUri || window.location.href;
                    if (redirectUri) {
                        safeLocalStorage.setItem('react_app_redirect_after_login', target);
                    }
                    liff.login({
                        redirectUri: target,
                    });
                    return null;
                }
                response.token = liff.getAccessToken();
                response.liffId = lineLiffId;
            } else {
                const target = redirectUri || window.location.href;
                if (redirectUri) {
                    safeLocalStorage.setItem('react_app_redirect_after_login', target);
                }
                liff.login({
                    redirectUri: target,
                });
            }
        } catch (err) {
            console.log("onInitLineLiff", err)
        }
    } else {
        console.log("onLoginLineLiff: No lineLiffId found");
    }
    return response;
}
async function onLogoutLineLiff() {
    const lineLiffId = process.env.REACT_APP_LINE_LIFF_ID || "";
    if (lineLiffId) {
        try {
            await liff.init({
                liffId: `${lineLiffId}`,
                withLoginOnExternalBrowser: true,
            }).catch(err => {
                throw err
            });
            if (liff.isLoggedIn()) {
                liff.logout();
            }
        } catch (err) {
            console.log("onLogoutLineLiff", err)
        }
    }
}
async function onCloseLineLiff() {
    liff.closeWindow();
}
async function onCheckFriendStatusLineLiff() {
    const lineLiffId = process.env.REACT_APP_LINE_LIFF_ID || "";
    const lineOAId = process.env.REACT_APP_LINE_OA_ID || "";
    if (lineLiffId && lineOAId) {
        try {
            await liff.init({
                liffId: `${lineLiffId}`,
                withLoginOnExternalBrowser: true,
            }).catch(err => {
                throw err
            });
            if (liff.isLoggedIn()) {
                const friendship = await liff.getFriendship();
                if (!friendship.friendFlag) {
                    const currentUrl = encodeURIComponent(window.location.href);
                    let addFriendUrl = `https://line.me/R/ti/p/${lineOAId}?redirect=${currentUrl}`;
                    addFriendUrl = `https://line.me/R/ti/p/${lineOAId}`;
                    window.location.href = addFriendUrl;
                }
            }
        } catch (err) {
            console.log("onCheckFriendStatus", err)
        }
    }
}

function formatDateByString(date, format) {
    dayjs.extend(buddhistEra);
    let newFormat = format.replace(/\bYYYY\b/g, "BBBB");
    return dayjs(date).locale('th').format(newFormat);
};

function checkFormatEvaluationFormData(param) {
    let check = false;
    let detail = {};
    if (Array.isArray(param)) {
        for (let item of param) {
            if (item.title) {
                detail.title = item.title;
            }
            if (item.group) {
                detail.group = item.group;
            }
            if (item.question_title) {
                detail.question_title = item.question_title;
            }
            if (item.component_type) {
                detail.component_type = item.component_type;
            }
        }
    }
    if (detail.title !== undefined
        && detail.group !== undefined
        && detail.question_title !== undefined
        && detail.component_type !== undefined
    ) {
        check = true;
    }
    return check;
};

function formatExceltoJSON(param) {
    let sheetData = null
    try {
        const workbook = XLSX.read(param, { type: 'binary' });
        const sheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];
        sheetData = XLSX.utils.sheet_to_json(sheet);
    } catch { }
    return sheetData;
};

function onGroupBy(arr, key) {
    return arr.reduce(function (rv, x) {
        (rv[x[key]] = rv[x[key]] || []).push(x);
        return rv;
    }, {});
};

function validatePassword(password) {
    const regex = /^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d]{8,}$/;
    return regex.test(password);
}

function getDuplicates(array) {
    const duplicates = [];

    for (let i = 0; i < array.length; i++) {
        if (array.indexOf(array[i]) !== i && !duplicates.includes(array[i])) {
            duplicates.push(array[i]);
        }
    }

    return duplicates;
}

function isWithinRadius(centerLat, centerLon, pointLat, pointLon, radius) {
    const toRadians = (degrees) => {
        return degrees * (Math.PI / 180);
    };
    const haversineDistance = (lat1, lon1, lat2, lon2) => {
        const R = 6371000; // Radius of the Earth in meters
        const dLat = toRadians(lat2 - lat1);
        const dLon = toRadians(lon2 - lon1);
        const a =
            Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.cos(toRadians(lat1)) *
            Math.cos(toRadians(lat2)) *
            Math.sin(dLon / 2) *
            Math.sin(dLon / 2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        const distance = R * c;
        return distance;
    };
    const distance = haversineDistance(
        centerLat,
        centerLon,
        pointLat,
        pointLon
    );
    return distance <= radius;
}


export {
    onSleep,
    capitalizeFirstLetter,
    onLoginLineLiff,
    onLogoutLineLiff,
    onCloseLineLiff,
    onCheckFriendStatusLineLiff,
    formatDateByString,
    checkFormatEvaluationFormData,
    formatExceltoJSON,
    onGroupBy,
    validatePassword,
    getDuplicates,
    isWithinRadius,
    safeLocalStorage,
}